'use client';

import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  Portal,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { memo, useCallback, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  LuArrowUp,
  LuAudioLines,
  LuChevronDown,
  LuChevronRight,
  LuPaperclip,
  LuPlug,
  LuPlus,
  LuSearch,
  LuX,
} from 'react-icons/lu';

import { useAutoResizeTextarea } from 'hooks/useAutoResizeTextarea';

const MIN_ROWS = 1;
const FILE_ACCEPT = 'image/*,.pdf,.csv,.xls,.xlsx,.doc,.docx,.txt';
const MAX_STACKED_ICONS = 4;

interface ConnectorOption {
  id: string;
  label: string;
  icon: IconType;
}

// Connectable sources surfaced under the "+" → Connectors submenu. Datana is the
// only live integration today; add more options here as they come online.
const CONNECTORS: ConnectorOption[] = [{ id: 'datana', label: 'Datana ERP', icon: LuPlug }];

interface AttachmentChipProps {
  name: string;
  index: number;
  onRemove: (index: number) => void;
}

const AttachmentChip = memo(({ name, index, onRemove }: AttachmentChipProps) => {
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <Flex
      alignItems="center"
      gap={1}
      pl={3}
      pr={1}
      py={1}
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="full"
      maxW="220px"
    >
      <LuPaperclip size="14px" />
      <Text fontSize="xs" color="text.primary" truncate>
        {name}
      </Text>
      <IconButton
        aria-label="Remove attachment"
        variant="ghost"
        size="2xs"
        color="text.secondary"
        borderRadius="full"
        _hover={{ bg: 'surface.hover' }}
        onClick={handleRemove}
      >
        <LuX />
      </IconButton>
    </Flex>
  );
});
AttachmentChip.displayName = 'AttachmentChip';

interface ConnectorMenuItemProps {
  connector: ConnectorOption;
  isChecked: boolean;
  onToggle: (id: string) => void;
}

const ConnectorMenuItem = memo(({ connector, isChecked, onToggle }: ConnectorMenuItemProps) => {
  const handleToggle = useCallback(() => {
    onToggle(connector.id);
  }, [connector.id, onToggle]);

  const Icon = connector.icon;

  return (
    <Menu.CheckboxItem
      value={connector.id}
      checked={isChecked}
      onCheckedChange={handleToggle}
      closeOnSelect={false}
      cursor="pointer"
      borderRadius="md"
      _highlighted={{ bg: 'surface.hover' }}
    >
      <Flex alignItems="center" gap={2}>
        <Icon />
        <Text fontSize="sm">{connector.label}</Text>
      </Flex>
      <Menu.ItemIndicator color="selection.500" />
    </Menu.CheckboxItem>
  );
});
ConnectorMenuItem.displayName = 'ConnectorMenuItem';

interface ConnectorMenuListProps {
  connectors: ConnectorOption[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

const ConnectorMenuList = memo(({ connectors, selectedIds, onToggle }: ConnectorMenuListProps) => (
  <>
    {connectors.map((connector) => (
      <ConnectorMenuItem
        key={connector.id}
        connector={connector}
        isChecked={selectedIds.includes(connector.id)}
        onToggle={onToggle}
      />
    ))}
  </>
));
ConnectorMenuList.displayName = 'ConnectorMenuList';

interface ConnectorsPillProps {
  connectors: ConnectorOption[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

const ConnectorsPillContent = memo(({ connectors }: { connectors: ConnectorOption[] }) => {
  if (connectors.length === 1) {
    const only = connectors[0];
    const Icon = only.icon;
    return (
      <>
        <Icon />
        <Text fontSize="sm">{only.label}</Text>
      </>
    );
  }

  return (
    <>
      <HStack gap={0}>
        {connectors.slice(0, MAX_STACKED_ICONS).map((connector, i) => {
          const Icon = connector.icon;
          return (
            <Flex
              key={connector.id}
              alignItems="center"
              justifyContent="center"
              boxSize="18px"
              borderRadius="full"
              bg="surface.accent"
              color="text.primary"
              ml={i === 0 ? 0 : '-6px'}
            >
              <Icon size="11px" />
            </Flex>
          );
        })}
      </HStack>
      <Text fontSize="sm">Sources</Text>
      <Text fontSize="sm" color="text.secondary">
        {connectors.length}
      </Text>
    </>
  );
});
ConnectorsPillContent.displayName = 'ConnectorsPillContent';

const ConnectorsPill = memo(({ connectors, selectedIds, onToggle }: ConnectorsPillProps) => {
  const selected = connectors.filter((c) => selectedIds.includes(c.id));
  if (selected.length === 0) {
    return null;
  }

  return (
    <Menu.Root positioning={{ placement: 'bottom-start' }}>
      <Menu.Trigger asChild>
        <Flex
          alignItems="center"
          gap={2}
          px={3}
          h="32px"
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="full"
          color="text.primary"
          cursor="pointer"
          _hover={{ bg: 'surface.hover' }}
        >
          <ConnectorsPillContent connectors={selected} />
        </Flex>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content bgColor="surface.background" p={2} borderRadius="lg" minW="220px">
            <ConnectorMenuList
              connectors={connectors}
              selectedIds={selectedIds}
              onToggle={onToggle}
            />
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
});
ConnectorsPill.displayName = 'ConnectorsPill';

interface AssistantComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
}

const AssistantComposer = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Ask anything…',
}: AssistantComposerProps) => {
  const { textareaRef, resize } = useAutoResizeTextarea(value);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedConnectorIds, setSelectedConnectorIds] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Show the send affordance whenever the user is composing (focused or has text);
  // fall back to the voice affordance only when the box is idle and empty.
  const showSend = isFocused || value.trim().length > 0;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      resize();
    },
    [onChange, resize],
  );

  // Keep the caret in the box after sending (the button click would otherwise
  // steal focus, and the re-render can drop it on the Enter path).
  const handleSubmit = useCallback(() => {
    if (value.trim() !== '') {
      onSubmit(value);
      // Attachments are consumed by the message; selected connectors persist as a
      // sticky source selection across messages.
      setAttachments([]);
    }
    textareaRef.current?.focus();
  }, [onSubmit, value, textareaRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleOpenFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFilesSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected == null || selected.length === 0) {
      return;
    }
    setAttachments((prev) => [...prev, ...Array.from(selected)]);
    // Reset so picking the same file again still fires onChange.
    e.target.value = '';
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleToggleConnector = useCallback((id: string) => {
    setSelectedConnectorIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((existing) => existing !== id);
      }
      return [...prev, id];
    });
  }, []);

  return (
    <Box
      w="full"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="2xl"
      bg="surface.background"
      px={4}
      pt={4}
      pb={3}
      _focusWithin={{ borderColor: 'input.borderHover' }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        hidden
        onChange={handleFilesSelected}
      />
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        resize="none"
        rows={MIN_ROWS}
        p={0}
        minH="unset"
        border="none"
        fontSize="md"
        color="text.primary"
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _placeholder={{ color: 'text.placeholder' }}
      />
      {attachments.length > 0 ? (
        <Flex wrap="wrap" gap={2} mt={3}>
          {attachments.map((file, index) => (
            <AttachmentChip
              key={`${file.name}-${index}`}
              name={file.name}
              index={index}
              onRemove={handleRemoveAttachment}
            />
          ))}
        </Flex>
      ) : null}
      <Flex alignItems="center" mt={3} gap={2}>
        <Menu.Root positioning={{ placement: 'bottom-start' }}>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Add files or connectors"
              variant="ghost"
              size="sm"
              color="text.secondary"
              borderRadius="full"
              _hover={{ bg: 'surface.hover' }}
            >
              <LuPlus />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content bgColor="surface.background" p={2} borderRadius="lg" minW="240px">
                <Menu.Item
                  value="upload"
                  cursor="pointer"
                  borderRadius="md"
                  _highlighted={{ bg: 'surface.hover' }}
                  onClick={handleOpenFilePicker}
                >
                  <Flex alignItems="center" gap={2}>
                    <LuPaperclip />
                    <Text fontSize="sm">Upload files or images</Text>
                  </Flex>
                </Menu.Item>
                <Menu.Root positioning={{ placement: 'right-start', gutter: 4 }}>
                  <Menu.TriggerItem
                    cursor="pointer"
                    borderRadius="md"
                    _highlighted={{ bg: 'surface.hover' }}
                  >
                    <Flex alignItems="center" gap={2} w="full">
                      <LuPlug />
                      <Text fontSize="sm">Connectors</Text>
                      <Box ml="auto" color="text.secondary">
                        <LuChevronRight />
                      </Box>
                    </Flex>
                  </Menu.TriggerItem>
                  <Portal>
                    <Menu.Positioner>
                      <Menu.Content
                        bgColor="surface.background"
                        p={2}
                        borderRadius="lg"
                        minW="220px"
                      >
                        <ConnectorMenuList
                          connectors={CONNECTORS}
                          selectedIds={selectedConnectorIds}
                          onToggle={handleToggleConnector}
                        />
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        <Button
          variant="outline"
          size="sm"
          borderColor="border.light"
          color="text.primary"
          borderRadius="full"
          _hover={{ bg: 'surface.hover' }}
        >
          <LuSearch />
          <Text fontSize="sm">Search</Text>
          <LuChevronDown />
        </Button>
        <ConnectorsPill
          connectors={CONNECTORS}
          selectedIds={selectedConnectorIds}
          onToggle={handleToggleConnector}
        />
        <HStack ml="auto" gap={2}>
          <Button
            variant="ghost"
            size="sm"
            color="text.secondary"
            borderRadius="full"
            _hover={{ bg: 'surface.hover' }}
          >
            <Text fontSize="sm">Model</Text>
            <LuChevronDown />
          </Button>
          <IconButton
            aria-label={showSend ? 'Send' : 'Voice'}
            size="sm"
            borderRadius="full"
            bg="surface.inverse"
            color="text.inverse"
            _hover={{ opacity: 0.9 }}
            onClick={handleSubmit}
          >
            {showSend ? <LuArrowUp /> : <LuAudioLines />}
          </IconButton>
        </HStack>
      </Flex>
    </Box>
  );
};

AssistantComposer.displayName = 'AssistantComposer';

export default memo(AssistantComposer);
